import React, { useReducer, useState, useEffect } from "react";
import PropTypes from "prop-types";
import TransactionTable from "../../elements/Tables/TransactionTable/TransactionTable";
import { useFetch } from "../../../Utils/useFetch";
import {
  FETCH_ALL_DEPT_API,
  FETCH_ALL_DISCOUNT_API,
  FETCH_ALL_TRANSACTION_API,
} from "../../../Constants/Apis";
import { Button, Input, message, Modal, Select, DatePicker, Form } from "antd";
import { reducer, headers } from "../../../Utils/CommonUtils";
import axios from "axios";
import { deleteTransaction } from "../../../Services/TransactionService";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const TransactionPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    departmentFilter: userData?.counterCode === "ECOMMRI" ? "E-COMMERCE" : "",
    dateRange: {
      startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    },
    filteredInfo: {},
    sorter: {},
    current: 1,
    pageSize: 10,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [modalForm] = Form.useForm();

  const handleDelete = async (id) => {
    setTransactionId(id);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    history.push("/user/create-transaction");
  };

  const handleOk = async () => {
    try {
      await deleteTransaction(transactionId);
      message.success("Transaction deleted successfully");
      callReFetch();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Something went wrong, failed to delete transaction."
      );
    }
    setIsModalOpen(false);
  };

  // Prepare API parameters with proper department filtering
  const apiParams = {
    searchKeyword: state.searchKeyword,
    departmentFilter:
      userData?.counterCode === "ECOMMRI"
        ? "E-COMMERCE" // ECOMMRI users can only see E-COMMERCE
        : state.departmentFilter === "E-COMMERCE"
        ? ""
        : state.departmentFilter, // Non-ECOMMRI users cannot see E-COMMERCE
    dateRange: state.dateRange,
    filteredInfo: state.filteredInfo,
    sorter: state.sorter,
    current: state.current,
    pageSize: state.pageSize,
    counterCode: userData?.counterCode || "",
  };

  const { fetchedData, callReFetch, loading } = useFetch(
    `${FETCH_ALL_TRANSACTION_API}/article`,
    apiParams
  );

  // Extract data and pagination info from the response
  const data = fetchedData?.data?.data || [];
  const paginationInfo = fetchedData?.data?.pagination || {
    current: 1,
    pageSize: 10,
    total: 0,
  };

  const {
    fetchedData: { data: discountData },
  } = useFetch(`${FETCH_ALL_DISCOUNT_API}`, state);

  const {
    fetchedData: { data: deptData = [] },
  } = useFetch(`${FETCH_ALL_DEPT_API}`, state);

  const handleSearch = (value) => {
    if (state.searchKeyword !== value) {
      dispatch({ type: "searchKeyword", payload: value });
      callReFetch();
    }
  };

  const handleDeptFilterChange = (value) => {
    if (state.departmentFilter !== value) {
      dispatch({ type: "departmentFilter", payload: value });
      callReFetch();
    }
  };

  const handleDateChange = (dates) => {
    const [startDate, endDate] = dates || [];
    const formattedStartDate = startDate
      ? startDate.format("YYYY-MM-DD")
      : null;
    const formattedEndDate = endDate ? endDate.format("YYYY-MM-DD") : null;

    const range = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };

    dispatch({ type: "dateRange", payload: range });
    callReFetch();
  };

  const handleDownloadExcel = async () => {
    try {
      // Prepare API parameters for downloading all data without pagination
      const downloadParams = {
        searchKeyword: state.searchKeyword,
        departmentFilter:
          userData?.counterCode === "ECOMMRI"
            ? "E-COMMERCE" // ECOMMRI users can only see E-COMMERCE
            : state.departmentFilter === "E-COMMERCE"
            ? ""
            : state.departmentFilter, // Non-ECOMMRI users cannot see E-COMMERCE
        dateRange: state.dateRange,
        filteredInfo: state.filteredInfo,
        sorter: state.sorter,
        current: 1,
        pageSize: 999999,
        counterCode: userData?.counterCode || "",
      };

      // Use the same fetch mechanism as the table data
      const response = await axios.get(`${FETCH_ALL_TRANSACTION_API}/article`, {
        params: downloadParams,
        ...headers(),
      });

      const allData = response?.data?.data || [];

      if (!allData || allData.length === 0) {
        message.warning("No transactions to download");
        return;
      }

      // Create header rows
      const headerRows = [
        {
          "Date Range":
            state.dateRange.startDate && state.dateRange.endDate
              ? `${state.dateRange.startDate} to ${state.dateRange.endDate}`
              : "All Dates",
        },
        {}, // Empty row
        {
          "Transaction Date": "Transaction Date",
          "Counter Department": "Counter Department",
          "Counter Code": "Counter Code",
          "Counter Name": "Counter Name",
          "No Bon": "No Bon",
          Division: "Division",
          Category: "Category",
          Subcategory: "Subcategory",
          Type: "Type",
          Barcode: "Barcode",
          Brand: "Brand",
          Article: "Article",
          Color: "Color",
          Size: "Size",
          Quantity: "Quantity",
          Price: "Price",
          "Discount Rupiah": "Discount Rupiah",
          "Discount %": "Discount %",
          Total: "Total",
          Keterangan: "Keterangan",
          "Problematic?": "Problematic?",
          HPP: "HPP",
        },
      ];

      const formattedData = allData.map((item) => ({
        "Transaction Date": item.transactionDate
          ? new Date(item.transactionDate)
          : "N/A",
        "Counter Department": item.counterDept,
        "Counter Code": item.counterCode,
        "Counter Name": item.counterName,
        "No Bon": item.noBon,
        Division: item.articleDetails?.division,
        Category: item.articleDetails?.category,
        Subcategory: item.articleDetails?.subcategory,
        Type: item.articleDetails?.type,
        Barcode: item.barcode,
        Brand: item.articleDetails?.brand,
        Article: item.article,
        Color: item.color,
        Size: item.size,
        Quantity: item.quantity,
        Price: item.price ? item.price : 0,
        "Discount Rupiah": item.discountRupiah ? item.discountRupiah : 0,
        "Discount %": item.discount ? item.discount + "%" : "",
        Total: item.total ? item.total : 0,
        Keterangan: item.keterangan,
        "Problematic?": item.isBermasalah ? "Yes" : "No",
        HPP: item.hpp,
      }));

      // Combine header rows with data
      const excelData = [...headerRows, ...formattedData];

      const worksheet = XLSX.utils.json_to_sheet(excelData, {
        skipHeader: true,
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
      XLSX.writeFile(workbook, "Transactions.xlsx");

      message.success(`Downloaded ${allData.length} transactions successfully`);
    } catch (error) {
      message.error("Failed to download Excel file");
      console.error("Download error:", error);
    }
  };

  return (
    <div
      style={{
        width: "100%",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1200px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Search
          placeholder="Search..."
          allowClear
          onSearch={handleSearch}
          style={{ width: "200px" }}
        />
        <Select
          value={state.departmentFilter}
          style={{ width: "200px" }}
          onChange={handleDeptFilterChange}
          disabled={userData?.counterCode === "ECOMMRI"}
        >
          <Option value="">All Departments</Option>
          {deptData
            .filter((dept) => {
              // If user is ECOMMRI, only show E-COMMERCE
              if (userData?.counterCode === "ECOMMRI") {
                return dept.name === "E-COMMERCE";
              }
              // If user is not ECOMMRI, exclude E-COMMERCE
              return dept.name !== "E-COMMERCE";
            })
            .map((dept) => (
              <Option key={dept._id} value={dept.name}>
                {dept.name}
              </Option>
            ))}
        </Select>
        <RangePicker
          onChange={handleDateChange}
          value={[
            dayjs(state.dateRange.startDate),
            dayjs(state.dateRange.endDate),
          ]}
          allowClear={false}
          disabledDate={(current) => {
            // Disable future dates
            const oneYearFromNow = dayjs().add(1, "month");
            return current && current > oneYearFromNow;
          }}
        />
        <Button type="default" onClick={handleDownloadExcel}>
          Download Excel
        </Button>
      </div>

      <TransactionTable
        history={history}
        loading={loading}
        data={data}
        callReFetch={callReFetch}
        dispatch={dispatch}
        handleDelete={handleDelete}
        discountData={discountData}
        modalForm={modalForm}
        isAdmin={userData?.roles?.includes("admin")}
        pagination={{
          current: paginationInfo.current,
          pageSize: paginationInfo.pageSize,
          total: paginationInfo.total,
          onChange: (page, pageSize) => {
            dispatch({ type: "current", payload: page });
            dispatch({ type: "pageSize", payload: pageSize });
            callReFetch();
          },
        }}
      />

      <Modal
        title="Delete Transaction"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
      >
        <p>Are you sure you want to delete this transaction?</p>
      </Modal>
    </div>
  );
};

TransactionPage.propTypes = {};

export default TransactionPage;
